// UI
import LineItemsList from '../ui/LineItemsList'
import Shipment from '../ui/Shipment'
import InvlevelHistory from '../ui/InvlevelHistory'
import Box from '../ui/Box'
import Badge from '../ui/Badge'

// Mantine
import { Menu, ActionIcon } from '@mantine/core'

// Dayjs
import dayjs from 'dayjs'

// Icons
import {
    IconDotsVertical,
    IconDownload,
    IconExternalLink,
    IconHistory,
    IconPrinter,
} from '@tabler/icons-react'

// Hooks
import useUserLocation from '@/hooks/useUserLocation'
import { useAxiosApi } from '@/hooks/useAxiosApi'
import { DOCUMENTS_URL } from '@/lib/constants'

import { BACKOFFICE_API_URL } from '@/lib/constants'

export default function FulfillmentSmallDisplay({ fulfillment, order }) {
    const statusesMap = {
        ATTEMPTED_DELIVERY: { color: 'yellow', label: 'Livraison tentée' },
        DELAYED: { color: 'orange', label: 'Retardé' },
        FULFILLED: { color: 'green', label: 'Traité' },
        CANCELED: { color: 'red', label: 'Annulé' },
        NOT_DELIVERED: { color: 'red', label: 'Non livré' },
        IN_TRANSIT: { color: 'blue', label: 'En transit' },
        CONFIRMED: { color: 'blue', label: 'Confirmé' },
        DELIVERED: { color: 'green', label: 'Livré' },
        MARKED_AS_FULFILLED: { color: 'green', label: 'Marquée comme expédié' },
        OUT_FOR_DELIVERY: { color: 'teal', label: 'En cours de livraison' },
    }

    const status = statusesMap[fulfillment.display_status] ?? {
        color: 'gray',
        label: 'Statut inconnu',
    }

    const { userPrinters } = useUserLocation()
    const a4Printer = userPrinters.a4
    const { requestPost } = useAxiosApi()

    const hasWatchClockDetails =
        fulfillment.fulfillment_line_items.some(
            (line) =>
                line.shopify_product_variant?.wcd_jewels && line.unit_price
        ) && fulfillment.shipment

    const printWatchWorksheet = async () => {
        await requestPost(`/fulfillments/${fulfillment.id}/print-watch-worksheet`, {
            printnode_ids: a4Printer.printnode_ids,
        })
    }

    const handleDownload = async () => {
        const endpoint = `${BACKOFFICE_API_URL}/fulfillments/watch-worksheet-xlsx/${fulfillment.id}.xlsx`

        const response = await fetch(endpoint)

        if (response.ok) {
            const blob = await response.blob()
            const url = window.URL.createObjectURL(new Blob([blob]))
            const a = document.createElement('a')
            a.href = url
            a.download = `watch_worksheet_${fulfillment.id}.xlsx`
            document.body.appendChild(a)
            a.click()
            a.remove()
        }
    }

    return (
        <Box
            title={`Préparation #${fulfillment.id}`}
            subtitle={`Préparée ${dayjs(fulfillment.created_at).calendar()} par ${fulfillment.location.name}`}
            rightSection={
                <div className="flex items-center gap-2">
                    <Badge color={status.color} size="sm">
                        {status.label}
                    </Badge>

                    <InvlevelHistory fulfillment_id={fulfillment.id}>
                        {(openHistory) => (
                            <Menu>
                                <Menu.Target>
                                    <ActionIcon variant="default" size={36}>
                                        <IconDotsVertical size={16} />
                                    </ActionIcon>
                                </Menu.Target>

                                <Menu.Dropdown>
                                    <Menu.Item
                                        leftSection={<IconHistory size={16} />}
                                        onClick={openHistory}
                                    >
                                        Historique du stock
                                    </Menu.Item>

                                    {hasWatchClockDetails && (
                                        <>
                                            <Menu.Item
                                                leftSection={
                                                    <IconDownload size={16} />
                                                }
                                                onClick={handleDownload}
                                            >
                                                US Breakdown Worksheet
                                            </Menu.Item>
                                            <Menu.Item
                                                component="a"
                                                target="_blank"
                                                href={`${DOCUMENTS_URL}/${fulfillment.public_token}/customs-worksheet.pdf`}
                                                leftSection={
                                                    <IconExternalLink size={16} />
                                                }
                                            >
                                                Watch Clock Details (PDF)
                                            </Menu.Item>
                                            {a4Printer && (
                                                <Menu.Item
                                                    leftSection={
                                                        <IconPrinter size={16} />
                                                    }
                                                    onClick={printWatchWorksheet}
                                                >
                                                    Imprimer WCD sur {a4Printer.name}
                                                </Menu.Item>
                                            )}
                                        </>
                                    )}
                                </Menu.Dropdown>
                            </Menu>
                        )}
                    </InvlevelHistory>
                </div>
            }
        >
            <LineItemsList
                lines={fulfillment.fulfillment_line_items}
                worksheetUrl={`${DOCUMENTS_URL}/${fulfillment.public_token}/customs-worksheet.pdf`}
            />

            {fulfillment.shipment && (
                <div className="mt-5">
                    <Shipment shipment={fulfillment.shipment} />
                </div>
            )}
        </Box>
    )
}
